import type { SymbolPickerState } from '../state/types';
import { stateToInstrument } from '../state/utils';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@thinkalpha/ui-core/utils';
import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'src/components/ui';
import { useEvent } from 'src/hooks/useEvent';
import { instrumentToString } from 'src/lib/instrumentUtils';

interface CopySymbolProps {
    stateInstrument: SymbolPickerState['instrument'];
    className?: string;
}

export const CopySymbol = ({ stateInstrument, className }: CopySymbolProps) => {
    const [isCopied, setIsCopied] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const instrument = stateToInstrument(stateInstrument);
    const symbolString = instrument ? instrumentToString(instrument) : '';

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleCopy = useEvent(async () => {
        try {
            await navigator.clipboard.writeText(symbolString);
            setIsCopied(true);

            // Clear any existing timeout before setting a new one
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                setIsCopied(false);
                timeoutRef.current = null;
            }, 3000);
        } catch {
            // Silently fail - user will notice copy didn't work
        }
    });

    const tooltipContent = (
        <div className="text-center">
            <div className="font-medium">{symbolString}</div>
            <div className={`text-xs ${isCopied ? 'text-green-400' : 'text-gray-400'}`}>
                {isCopied ? 'Copied!' : 'Click to copy'}
            </div>
        </div>
    );

    return (
        <Tooltip
            content={tooltipContent}
            asChild
            delayDuration={0}
            // Both onPointerDown and onClick need to prevent default and stop propagation
            // to prevent the tooltip from closing when the icon is clicked to copy
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCopy();
            }}
            onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            // reset copied message anytime tooltip opens or closes
            onOpenChange={() => {
                setIsCopied(false);
            }}
        >
            <FontAwesomeIcon icon={faCopy} size="xs" className={cn('text-gray-400', className)} />
        </Tooltip>
    );
};
